import api from './api';

/** One row of the v2→v1 charge cross-walk worklist (v2_charge_override). */
export interface ChargeMappingRow {
  recno: number;
  v2_charge_code: string;
  v2_description: string | null;
  occurrence_count: number;
  auto_match_status: 'matched' | 'ambiguous' | 'unmatched' | null;
  suggested_recno: number | null;
  suggested_description: string | null;
  additionalcharges_recno: number | null;
  approved_description: string | null;
  approved: number;            // 0/1
  approved_by: string | null;
  approved_datetime: string | null;
}

export interface CatalogRow {
  recno: number;
  description: string;
  ratetype: string;
  direct: string;
  tariff: string;
}

/** One row of the v2→v1 rate-zone cross-walk worklist (v2_ratezone_mapping). */
export interface RateZoneMappingRow {
  v2_zone: string;
  v1_ratezone: string | null;   // null/'' = unmapped
  occurrence_count: number;
  auto_prefilled: number;       // 0/1 — 1 when the sync auto-filled from an exact match
  mapped_by: string | null;
  mapped_datetime: string | null;
}

/** Result of POST /sync-ratezones. */
export interface RateZoneSyncResult {
  synced: number;
  prefilled: number;
  unmapped: number;
}

export interface MigrationStats {
  total: number;
  approved: number;
  auto_matched: number;
  needs_review: number;
}

/** Migrated vs human-verified quote counts (so we know when all are done). */
export interface VerificationStats {
  migrated: number;
  verified: number;
  remaining: number;
}

export interface ActivePair {
  v2quotenumber: number;
  v2lane: string;
  /** Migrated by the TOOL (a v2_quote_verification row) — drives the Migrated count. */
  migrated?: boolean;
  /** A RECENT v1 quote (rev 1 on/after the migration-match cutoff, 2025-06-01) already
   *  exists for this lane — a genuine migration, manual OR tool. `inV1 && !migrated` =
   *  created manually → "Already in V1" (off the actionable list). A pre-cutoff
   *  cross-referenced quote is a stray legacy match: it is ignored (inV1=false), so the
   *  lane still shows as "To migrate" even though an old quote exists in the account. */
  inV1?: boolean;
  /** The v2 lane is CANCELLED (Status==4). Still migrates (UNSECURED), but the
   *  worklist badges it so the reviewer knows. Best-effort: false if v2 was unreachable. */
  cancelled?: boolean;
  quotenumber?: number | null;
  revisionnumber?: number | null;
  /** Whether an admin marked this lane "Not Migrating" (excluded from the worklist). */
  excluded?: boolean;
  reason?: string | null;
  excludedBy?: string | null;
  excludedDate?: string | null;
}

/** A migrated v1 quote still awaiting human verification (worklist tab). */
export interface MigratedUnverifiedRow {
  quotenumber: number;
  revisionnumber: number;
  v2quotenumber: number;
  v2lane: string | null;
}

/** One row of the full migration history (every quote this tool migrated). */
export interface MigrationHistoryRow {
  quotenumber: number;
  revisionnumber: number;
  v2quotenumber: number;
  v2lane: string | null;
  migratedAt: string | null;   // v2_quote_verification.created_at (ISO) — when migrated
  verified: boolean;
  verifiedBy: string | null;   // who verified in 360 (null until verified)
  verifiedAt: string | null;
  customerName: string | null;
  billtoName: string | null;
}

export interface DrilldownCharge {
  v2_charge_code: string;
  v2_description: string | null;
  addlchargeratetype: string;
  addlchargerate: number;
  freetime: number;
  freetimeunit: string;
  two_tier: boolean;
  additionalcharges_recno: number | null;
  match_status: 'matched' | 'override' | 'ambiguous' | 'unmatched' | 'skip';
}

/** The full proposed v1 quote rows (masterquote / quoterevisions / directquote)
 *  as field→value maps, plus the charge lines and any unmappable-field flags. */
export interface QuoteDrilldown {
  v2quotenumber: number;
  v2lane: string;
  v2EffectiveDate?: string | null;
  v2ExpirationDate?: string | null;
  /** Resolved party names for the header (billto → billtos.cname, customer → customers.custname). */
  billtoName?: string | null;
  customerName?: string | null;
  master: Record<string, string | number | null>;
  revision: Record<string, string | number | null>;
  direct: Record<string, string | number | null>;
  flags: string[];
  charges: DrilldownCharge[];
}

export interface MigrateResult {
  executed: boolean;
  schema: string;
  active: number;
  skipped_existing: number;
  created: number;
  /** Pairs skipped because they're marked "Not Migrating". */
  excluded?: number;
  total_charges: number;
  missing: string[];
  flagged: { pair: string; flags: string[] }[];
  quotes: { pair: string; quotenumber: number; charges: number }[];
  /** Already-migrated pairs in scope → the existing v1 quote they became, so the
   *  page can link straight to it instead of showing a dead "skipped". */
  skipped?: { pair: string; quotenumber: number; revisionnumber: number }[];
  status?: string;
}

/** The v2 quote numbers behind each worklist "Used" count, keyed by charge code
 *  (charges tab) and by pickup zone (rate-zone tab). Feeds the "Used" hover tooltip. */
export interface WorklistQuotes {
  charges: Record<string, number[]>;
  ratezones: Record<string, number[]>;
}

// One v2 (Azure SQL) read serves both tabs' tooltips; cache the promise so the
// charges and rate-zone views share a single fetch. Cleared on failure so a later
// hover can retry.
let _worklistQuotesPromise: Promise<WorklistQuotes> | null = null;

const quoteMigrationService = {
  listQuotes(): Promise<ActivePair[]> {
    return api.get('/admin/quote-migration/quotes').then((r) => r.data);
  },
  migratedUnverified(): Promise<MigratedUnverifiedRow[]> {
    return api.get('/admin/quote-migration/migrated-unverified').then((r) => r.data);
  },
  /** Full migration history — every tool-migrated quote (when + verify status). */
  migrationHistory(): Promise<MigrationHistoryRow[]> {
    return api.get('/admin/quote-migration/history').then((r) => r.data);
  },
  quoteDrilldown(v2quotenumber: number, v2lane: string): Promise<QuoteDrilldown> {
    return api.get(`/admin/quote-migration/quote/${v2quotenumber}/${encodeURIComponent(v2lane)}`).then((r) => r.data);
  },
  migrateOne(v2quotenumber: number, v2lane: string): Promise<MigrateResult> {
    return api.post(`/admin/quote-migration/quote/${v2quotenumber}/${encodeURIComponent(v2lane)}/migrate`).then((r) => r.data);
  },
  /** Mark a lane "Not Migrating" (exclude from the worklist + runner). */
  excludeQuote(v2quotenumber: number, v2lane: string, reason?: string): Promise<unknown> {
    return api.post(`/admin/quote-migration/quote/${v2quotenumber}/${encodeURIComponent(v2lane)}/exclude`,
      { reason: reason ?? null }).then((r) => r.data);
  },
  /** Restore a "Not Migrating" lane back to the worklist. */
  restoreQuote(v2quotenumber: number, v2lane: string): Promise<unknown> {
    return api.delete(`/admin/quote-migration/quote/${v2quotenumber}/${encodeURIComponent(v2lane)}/exclude`).then((r) => r.data);
  },
  runMigration(execute: boolean, limit = 0): Promise<MigrateResult> {
    return api.post('/admin/quote-migration/run', { execute, limit }).then((r) => r.data);
  },
  listCharges(): Promise<ChargeMappingRow[]> {
    return api.get('/admin/quote-migration/charges').then((r) => r.data);
  },
  catalog(): Promise<CatalogRow[]> {
    return api.get('/admin/quote-migration/catalog').then((r) => r.data);
  },
  stats(): Promise<MigrationStats> {
    return api.get('/admin/quote-migration/stats').then((r) => r.data);
  },
  verificationStats(): Promise<VerificationStats> {
    return api.get('/admin/quote-migration/verification-stats').then((r) => r.data);
  },
  /** Approve/save a mapping. recno=null + approved=true → explicit skip. */
  updateCharge(rowRecno: number, additionalcharges_recno: number | null, approved = true): Promise<unknown> {
    return api.put(`/admin/quote-migration/charges/${rowRecno}`,
      { additionalcharges_recno, approved }).then((r) => r.data);
  },
  // ── Rate-zone cross-walk ──
  listRatezones(): Promise<RateZoneMappingRow[]> {
    return api.get('/admin/quote-migration/ratezones').then((r) => r.data);
  },
  ratezoneCatalog(): Promise<string[]> {
    return api.get('/admin/quote-migration/ratezone-catalog').then((r) => r.data);
  },
  /** Scan the active set for distinct v2 zones and upsert the worklist. */
  syncRatezones(): Promise<RateZoneSyncResult> {
    return api.post('/admin/quote-migration/sync-ratezones').then((r) => r.data);
  },
  /** Set (or clear, v1_ratezone=null) a v2 zone → v1 rate zone mapping. */
  updateRatezone(v2_zone: string, v1_ratezone: string | null): Promise<unknown> {
    return api.put('/admin/quote-migration/ratezones', { v2_zone, v1_ratezone }).then((r) => r.data);
  },
  /** v2 quote numbers behind each charge/zone "Used" count (drill-down tooltip).
   *  Cached across both tabs; one v2 read per page load. */
  worklistQuotes(): Promise<WorklistQuotes> {
    if (!_worklistQuotesPromise) {
      _worklistQuotesPromise = api.get('/admin/quote-migration/worklist-quotes')
        .then((r) => r.data as WorklistQuotes)
        .catch((e) => { _worklistQuotesPromise = null; throw e; });
    }
    return _worklistQuotesPromise;
  },
};

export default quoteMigrationService;
